import {DigitalAssetVisual} from '@cohort/components-xps/components/DigitalAssetVisual';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import PrimaryButton from '@cohort/wallet/components/button/PrimaryButton';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import {PerkAccessesDetails} from '@cohort/wallet/components/space/nftTransfer/PerkAccessesDetails';
import {VisualWithDetails} from '@cohort/wallet/components/space/VisualWithDetails';
import {useCohortMutation} from '@cohort/wallet/hooks/api/Query';
import {useUserStore} from '@cohort/wallet/hooks/stores/user';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {nftObtentionDate} from '@cohort/wallet/lib/Utils';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {ArrowDown, FolderOpen} from '@phosphor-icons/react';
import React, {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

interface EmailTransferConfirmationProps {
  recipientEmail: string;
  emailMessage: string;
  ownership: OwnershipWDto;
  onEdit: () => void;
  onTransferSuccess: () => void;
}

export const EmailTransferConfirmation: React.FC<EmailTransferConfirmationProps> = ({
  recipientEmail,
  emailMessage,
  ownership,
  onEdit,
  onTransferSuccess,
}) => {
  const user = useUserStore(store => store.user);
  const {t} = useTranslation('components', {
    keyPrefix: 'space.nftTransfer.emailTransferConfirmation',
  });
  const {accentColor, hasDarkBg} = useThemeContext();
  const requestOwnershipTransfer = useApi(OwnershipsApi.requestOwnershipTransfer);

  const {mutate: handleSendViaEmail} = useCohortMutation({
    mutationFn: async () =>
      requestOwnershipTransfer(ownership.id, {
        recipientEmail,
        senderMessage: emailMessage,
      }),
    onSuccess: () => onTransferSuccess(),
  });

  const onEditClick = useCallback(() => {
    onEdit();
  }, [onEdit]);

  return (
    <Fragment>
      <h2 className={classNames('mb-2 text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}>
        {t('invitationTitle')}
      </h2>

      <div
        className={classNames(
          'rounded-lg border lg:w-3/4',
          hasDarkBg ? ' border-white/20' : ' border-black/20'
        )}
      >
        <div
          className={classNames(
            'flex justify-between border-b px-5 py-[10px]',
            hasDarkBg ? ' border-white/20' : ' border-black/20'
          )}
        >
          <div className="mr-3 flex space-x-3">
            <ArrowDown className="my-auto h-4 w-4" style={{color: accentColor}} />
            <span
              className={classNames(
                'my-auto text-sm font-semibold md:text-base md:font-normal',
                hasDarkBg ? 'text-white' : 'text-black/90'
              )}
            >
              {t('from')}
            </span>
          </div>
          <div
            data-testid="sender-email-address"
            className={classNames(
              'my-auto break-all text-sm md:text-base',
              hasDarkBg ? 'text-white/50' : 'text-gray-500'
            )}
          >
            {user?.email}
          </div>
        </div>

        <div className="flex justify-between px-5 py-[10px]">
          <div className="mr-3 flex space-x-3">
            <FolderOpen className="my-auto h-4 w-4" style={{color: accentColor}} />
            <div
              className={classNames(
                'my-auto text-sm font-semibold md:text-base md:font-normal',
                hasDarkBg ? 'text-white' : 'text-black/90'
              )}
            >
              {t('to')}
            </div>
          </div>
          <div
            data-testid="recipient-email-address"
            className={classNames(
              'my-auto break-all text-sm md:text-base',
              hasDarkBg ? 'text-white/50' : 'text-gray-500'
            )}
          >
            {recipientEmail}
          </div>
        </div>
      </div>

      <p className={classNames('mt-2 text-sm', hasDarkBg ? 'text-white/50' : 'text-gray-500')}>
        {t('invitationHelperText')}
      </p>

      <div className="mt-10 md:hidden">
        <VisualWithDetails
          visual={
            <DigitalAssetVisual
              digitalAssetImageFileKey={ownership.digitalAsset.imageFileKey}
              animationFileKey={ownership.digitalAsset.animationFileKey}
            />
          }
          title={ownership.digitalAsset.collection.title}
        >
          <div
            className={classNames(
              hasDarkBg ? 'text-white/50' : 'text-gray-500',
              'inline-flex w-full items-center text-sm leading-tight'
            )}
          >
            {nftObtentionDate(ownership)}
          </div>
        </VisualWithDetails>
        <div className="mt-5">
          <PerkAccessesDetails
            perkAccesses={ownership.digitalAsset.perkAccesses}
            helperText={t('perkAccessListHelperText')}
          />
        </div>
      </div>

      {emailMessage.length > 0 && (
        <Fragment>
          <h2
            className={classNames('mb-1 mt-7 text-sm', hasDarkBg ? 'text-white' : 'text-black/90')}
          >
            {t('invitationMessageInputTitle')}
          </h2>

          <div
            className={classNames(
              'rounded-lg border p-5 lg:w-3/4',
              hasDarkBg ? 'text-white' : 'text-black/90'
            )}
          >
            {emailMessage}
          </div>
        </Fragment>
      )}

      <div className="mt-7 flex flex-col pb-7 md:flex-row md:pb-0">
        <div className="mb-3 w-full md:mb-0 md:mr-2 md:w-fit">
          <PrimaryButton
            testId="send-email-transfer-invitation"
            text={t('sendInvitationBtnText')}
            className="w-full md:w-fit md:min-w-[100px] lg:min-w-[165px]"
            onClick={() => handleSendViaEmail()}
            tracking={{namespace: 'digitalAssets.transfer.email.sendInvite'}}
          />
        </div>

        <SecondaryButton
          testId="edit-email-transfer-invitation"
          text={t('editInvitationBtnText')}
          onClick={onEditClick}
          className="w-full md:ml-2 md:w-fit md:min-w-[100px] lg:min-w-[165px]"
          tracking={{namespace: 'digitalAssets.transfer.email.updateInvite'}}
        />
      </div>
    </Fragment>
  );
};
